/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState } from 'react'
import CustomHeader from '../../components/CustomHeader'
import { useAppDispatch } from '../../store'
import { getDivisionList, getDivisionDetails, getBreadcrumbList } from '../../store/slices/reportHierarchySlice/apis'
import { divisionDetails, divisionList, getBreadcrumbListData, getLoading, getReportUpdateState, isReportHierarchyHavingError, isDivisionDetailsLoading, isDivisionListLoading } from '../../store/slices/reportHierarchySlice/selectors'
import PAGE_TITLES from '../../consts/titles'
import { useParams } from 'react-router-dom'
import DivisionTable from '../../features/ReportHierarchyFeature/Table/'
import { resetState } from '../../store/slices/reportHierarchySlice'
import ROUTES from '../../consts/paths'
import { getInstructors } from '../../store/slices/instructorSlice/apis'
import isEmpty from 'lodash/isEmpty'
import useAsyncEffect from 'use-async-effect'
import { type CustomBreadcrumbT, prepareBreadCrumbs } from '../../utils/BreadcrumbDataPreparation'
import { type ApplyFiltersAndDispatchParams, Actions } from '../../components/CustomTable/types'
import { type GridSortItem } from '@mui/x-data-grid'
import PageHeader from '../../components/PageHeader'
import LoadingPageV2 from '../LoadingPageV2'
import { Box, Tooltip, Typography } from '@mui/material'
import { AccountBalanceOutlined } from '@mui/icons-material'
import GenericButton from '../../components/GenericButton'
import GenericModal from '../../components/GenericModal'
import CreateEditDivision from '../../features/ReportHierarchyFeature/CreateDivision'
import { CreateNewReportingHierarchy } from '../../features/ReportHierarchyFeature/CreateNewReportingHierarchyModal'

const breadcrumbPrefixList: CustomBreadcrumbT[] = [{
  title: 'Reporting Hierarchy',
  path: ROUTES.REPORTING_HIERARCHY.toString()
}]

const ReportHierarchyPage: React.FC = (): JSX.Element => {
  const [perPageSize, setPerPageSize] = useState<number>(10)
  const [search, setSearch] = useState<string>('')
  const [sortModel, setSortModel] = useState<GridSortItem>()
  const [openModal, setOpenModal] = useState(false)

  const { id } = useParams()

  const dispatch = useAppDispatch()
  const division = divisionDetails()
  const list = divisionList()
  const isLoading = isDivisionDetailsLoading()
  const isTableLoading = isDivisionListLoading()
  const isError = isReportHierarchyHavingError()
  const breadcrumbData = getBreadcrumbListData()
  const updateState = getReportUpdateState()
  const preparedBreadcrumbs = prepareBreadCrumbs(breadcrumbData ?? [], ROUTES.REPORTING_HIERARCHY)
  const breadcrumb = (breadcrumbData !== undefined && breadcrumbData !== null && breadcrumbData.length > 0)
    ? breadcrumbData[breadcrumbData.length - 1]
    : null

  useAsyncEffect(async () => {
    dispatch(resetState())
    await dispatch(getBreadcrumbList({ urlId: id ?? '' }))
  }, [id])

  useAsyncEffect(async () => {
    if (breadcrumb != null) {
      // void dispatch(getInstructors({}))
      void dispatch(getDivisionDetails({ urlId: breadcrumb.id }))

      if (breadcrumb.model === 'DIVISION') {
        const params = { pageSize: perPageSize }
        void dispatch(getDivisionList({ urlId: breadcrumb.id, params }))
      }
    }
  }, [breadcrumbData])

  useAsyncEffect(async () => {
    if (breadcrumb != null && breadcrumb.model === 'DIVISION') {
      const params = {
        search,
        sort: sortModel ? JSON.stringify({ field: sortModel.field, direction: sortModel.sort }) : undefined,
        pageSize: perPageSize
      }
      void dispatch(getDivisionList({ urlId: breadcrumb.id, params }))
    }
  }, [updateState])

  const isReportHierarchyReady = !isEmpty(division)

  const hasEditPermission = division?.actions?.includes(Actions.EDIT) ?? false

  const shouldShowCreateModal = !isLoading && isError && isEmpty(division)

  const applyFiltersAndDispatch = async ({
    pageSize,
    pageNumber,
    search,
    sort: sortModel
  }: ApplyFiltersAndDispatchParams): Promise<void> => {
    const sort = sortModel && { field: sortModel.field, direction: sortModel.sort }
    const params = {
      search,
      sort: sortModel ? JSON.stringify(sort) : undefined,
      pageSize,
      pageNumber
    }

    void dispatch(getDivisionList({ urlId: breadcrumb?.id, params }))
  }

  const handleEditButtonClick = (): void => {
    setOpenModal(true)
  }

  const handleRowsPerPageChange = async (pageSize: number): Promise<void> => {
    setPerPageSize(pageSize)
    await applyFiltersAndDispatch({ pageSize, search, sort: sortModel })
  }

  const handleSearch = async (value: string): Promise<void> => {
    setSearch(value)
    await applyFiltersAndDispatch({ pageSize: perPageSize, search: value, sort: sortModel })
  }

  const handlePageChange = async (page: number): Promise<void> => {
    await applyFiltersAndDispatch({ pageSize: perPageSize, pageNumber: page, search, sort: sortModel })
  }

  const onSortModelChange = async (sortModel: GridSortItem): Promise<void> => {
    setSortModel(sortModel)
    await applyFiltersAndDispatch({ pageSize: perPageSize, search, sort: sortModel })
  }
  const allowedActions = list?.actions ?? []

  return (
    <>
      {isLoading && (
        <LoadingPageV2 title={PAGE_TITLES.REPORTING_HIERARCHY} />
      )}

      <CustomHeader title={PAGE_TITLES.REPORTING_HIERARCHY} />
      {!isLoading && isReportHierarchyReady && (
        <Box display={'flex'} flexDirection={'column'} gap={6}>
          <PageHeader
            title={`${division?.data?.title} (${division?.data?.abbreviation})`}
            breadcrumb={breadcrumbPrefixList.concat(preparedBreadcrumbs ?? [])}
            attributes={
              <Box display='flex' flexDirection='row' gap={2} alignItems='center'>
                <Tooltip title="Academic Unit" arrow>
                  <AccountBalanceOutlined aria-label='Academic Unit Type' />
                </Tooltip>
                <Typography tabIndex={0} variant="h3">{division?.data?.categoryName}
                </Typography>
              </Box >
            }
            controls={
              hasEditPermission && (
                <GenericButton
                  variant='text'
                  label='Edit'
                  onClick={handleEditButtonClick}
                />
              )
            }
          />
          <GenericModal
            isOpen={openModal}
            maxWidth='sm'
            title={'Edit Details'}
            onClose={() => {
              setOpenModal(false)
            }}
            action={false}
          >
            <CreateEditDivision handleCloseModal={() => { setOpenModal(false) }} selectedRow={division?.data} />
          </GenericModal>
          {
            !isEmpty(list) && breadcrumb?.model === 'DIVISION' &&
            <DivisionTable
              list={list}
              loading={isTableLoading}
              handlePageChange={(page: number) => { void handlePageChange(page) }}
              onSearch={(value: string) => { void handleSearch(value) }}
              onSortModelChange={(sortModel: GridSortItem) => { void onSortModelChange(sortModel) }}
              handleRowsPerPageChange={(pageSize: number) => { void handleRowsPerPageChange(pageSize) }}
              allowedActions={allowedActions}
              breadcrumbId={breadcrumb?.id}
              params={{ search, sort: sortModel, pageSize: perPageSize }}
            />
          }
        </Box>
      )}
      {
        shouldShowCreateModal &&
        <CreateNewReportingHierarchy />
      }

    </>
  )
}

export default ReportHierarchyPage
